import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { AccionesDialog } from '@models/base/identificadores.model';
import { CasoUsoDto } from '@models/seguridad/casosUso/casoUsoDto.model';
import { CreatePermisoRequest } from '@models/seguridad/permisos/createPermisoRequest.model';
import { PermisoDto } from '@models/seguridad/permisos/permisoDto.model';
import { RolDto } from '@models/seguridad/roles/rolDto.model';
import { CasoUsoService } from '@services/casoUso/casoUso.service';
import { PermisoService } from '@services/permiso/permiso.service';
import { RolService } from '@services/rol/rol.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { ButtonCancelDialogComponent } from '@component/button-cancel-dialog/button-cancel-dialog.component';
import { ButtonCloseDialogComponent } from '@component/button-close-dialog/button-close-dialog.component';
import { AppRolesDirective } from '@directives/appRoles/app-roles.directive';

@Component({
  selector: 'app-dialog-form-permiso',
  standalone: true,
  imports: [
    MatFormFieldModule, MatInputModule, ReactiveFormsModule, MatCardModule,
    MatIconModule, MatButtonModule, MatDividerModule, NgxSpinnerModule,
    MatTableModule, MatSliderModule, MatSlideToggleModule, CommonModule,
    MatMenuModule, MatDialogModule, MatDatepickerModule, MatExpansionModule,
    AppRolesDirective, MatSelectModule, MatOptionModule, ButtonCancelDialogComponent,
    ButtonCloseDialogComponent
  ],
  templateUrl: './dialog-form-permiso.component.html',
  styleUrl: './dialog-form-permiso.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFormPermisoComponent {
  formBuilder = new FormBuilder();
  permisoDto!: PermisoDto;
  casosUso!: CasoUsoDto[];
  roles!: RolDto[];

  get f() { return this.form.controls; }

  accionesDialog = AccionesDialog;
  form!: FormGroup;
  localData: any;

  constructor(
    public dialogRef: MatDialogRef<DialogFormPermisoComponent>,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private casoUsoService: CasoUsoService,
    private rolService: RolService,
    private permisoService: PermisoService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.localData = { ...data };
    this.initializeForm();
  }

  ngOnInit(): void {
    this.getAllRol();
    this.getAllCasosUso();
    this.setFormData();
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      Descripcion: ["", Validators.compose([Validators.required])],
      Token: ["", Validators.compose([Validators.required])],
      CasoUso: ["", Validators.compose([Validators.required])],
      Rol: ["", Validators.compose([Validators.required])],
    });
  }

  setFormData() {
    if (this.data.accion === AccionesDialog.Modificar && this.data.id) {
      this.form.patchValue({
        Descripcion: this.data.descripcion || '',
        Token: this.data.token || '',
        CasoUso: this.data.idCasoUso || '',
        Rol: this.data.idRol || ''
      });
    }
  }

  getAllRol() {
    this.spinner.show("spBusquedaRoles");
    this.rolService.getAll()
      .subscribe({
        next: (data) => {
          this.spinner.hide("spBusquedaRoles");
          this.roles = data;
        },
        error: (error) => {
          this.spinner.hide("spBusquedaRoles");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  getAllCasosUso() {
    this.spinner.show("spBusquedaCasosUso");
    this.casoUsoService.getAll()
      .subscribe({
        next: (data) => {
          this.spinner.hide("spBusquedaCasosUso");
          this.casosUso = data;
        },
        error: (error) => {
          this.spinner.hide("spBusquedaCasosUso");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  agregar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const createPermisoRequest: CreatePermisoRequest = new CreatePermisoRequest();
    createPermisoRequest.descripcion = this.form.controls["Descripcion"].value;
    createPermisoRequest.idCasoUso = this.form.controls["CasoUso"].value;
    createPermisoRequest.token = this.form.controls["Token"].value;
    createPermisoRequest.idRol = this.form.controls["Rol"].value;

    this.create(createPermisoRequest);
  }

  modificar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const updatePermisoRequest = {
      id: this.data.id,
      descripcion: this.form.controls["Descripcion"].value,
      idCasoUso: this.form.controls["CasoUso"].value,
      token: this.form.controls["Token"].value,
      idRol: this.form.controls["Rol"].value
    };

    this.update(updatePermisoRequest);
  }

  eliminar() {
    if (!this.data.id) {
      this.snackBar.showError("No se puede eliminar: ID no válido", "Error");
      return;
    }

    this.delete(this.data.id);
  }

  closeDialog() {
    this.dialogRef.close({ event: AccionesDialog.Cancelar });
  }

  create(createPermisoRequest: CreatePermisoRequest) {
    this.spinner.show("spCrearPermiso");
    this.permisoService.create(createPermisoRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spCrearPermiso");
          this.snackBar.showSuccess("Permiso creado correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Agregar, data });
        },
        error: (error) => {
          this.spinner.hide("spCrearPermiso");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  update(updatePermisoRequest: any) {
    this.spinner.show("spModificarPermiso");
    this.permisoService.update(updatePermisoRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spModificarPermiso");
          this.snackBar.showSuccess("Permiso modificado correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Modificar, data });
        },
        error: (error) => {
          this.spinner.hide("spModificarPermiso");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  delete(id: number) {
    this.spinner.show("spEliminarPermiso");
    this.permisoService.delete(id)
      .subscribe({
        next: () => {
          this.spinner.hide("spEliminarPermiso");
          this.snackBar.showSuccess("Permiso eliminado correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Eliminar });
        },
        error: (error) => {
          this.spinner.hide("spEliminarPermiso");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  private markFormGroupTouched() {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });
  }

  isFalsy(valor: any) {
    return isFalsy(valor);
  }

  trackByItems(index: number, item: any): any {
    return item.id || index;
  }
}
